import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Popup, Toast } from 'antd-mobile';
import {
  AddOutline,
  CloseOutline,
  EnvironmentOutline,
  FilterOutline,
  SearchOutline
} from 'antd-mobile-icons';
import SafeArea from '@/components/SafeArea';
import ListItem from '@/components/ListItem';
import Skeleton from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { getCustomers, CustomerListResponse } from '@/api/customers';
import useBackHandler, { BACK_PRIORITY } from '@/hooks/useBackHandler';
import './styles.css';

export default function Customers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const keywordFromUrl = searchParams.get('keyword') || '';
  const surnameFromUrl = searchParams.get('surname') || '';
  const villageFromUrl = searchParams.get('village') || '';
  const currentListQuery = searchParams.toString();
  const currentListPath = `/customers${currentListQuery ? `?${currentListQuery}` : ''}`;
  const [keyword, setKeyword] = useState(keywordFromUrl);
  const [debouncedKeyword, setDebouncedKeyword] = useState(keywordFromUrl);
  const [activeSurname, setActiveSurname] = useState(surnameFromUrl);
  const [activeVillage, setActiveVillage] = useState(villageFromUrl);
  const [draftSurname, setDraftSurname] = useState('');
  const [draftVillage, setDraftVillage] = useState(villageFromUrl);
  const [data, setData] = useState<CustomerListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);

  useBackHandler(
    filterVisible,
    () => setFilterVisible(false),
    BACK_PRIORITY.popup
  );

  const fetchData = async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await getCustomers(
        {
          keyword: debouncedKeyword,
          surname: activeSurname,
          village: activeVillage
        },
        signal
      );
      if (res.code === 0) setData(res.data);
    } catch (error: any) {
      if (error?.code !== 'ERR_CANCELED') {
        setLoadError(true);
        Toast.show({ content: '获取客户列表失败', icon: 'fail' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedKeyword(keyword.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [debouncedKeyword, activeSurname, activeVillage]);

  useEffect(() => {
    setKeyword(keywordFromUrl);
    setDebouncedKeyword(keywordFromUrl);
    setActiveSurname(surnameFromUrl);
    setActiveVillage(villageFromUrl);
  }, [keywordFromUrl, surnameFromUrl, villageFromUrl]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (debouncedKeyword) nextParams.set('keyword', debouncedKeyword);
    else nextParams.delete('keyword');
    if (activeSurname) nextParams.set('surname', activeSurname);
    else nextParams.delete('surname');
    if (activeVillage) nextParams.set('village', activeVillage);
    else nextParams.delete('village');

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeSurname, activeVillage, debouncedKeyword, searchParams, setSearchParams]);

  const openFilter = () => {
    setDraftSurname(activeSurname);
    setDraftVillage(activeVillage);
    setFilterVisible(true);
  };

  const applyFilter = () => {
    setActiveSurname(draftSurname);
    setActiveVillage(draftVillage);
    setFilterVisible(false);
  };

  const customerDetailPath = (customerId: number) => {
    return `/customer/${customerId}?returnTo=${encodeURIComponent(currentListPath)}`;
  };

  const count = data?.list.length || 0;

  return (
    <div className="ios-page customers-page">
      <section className="customers-hero">
        <SafeArea top={false} className="customers-safe-area">
          <header className="customers-header">
            <div>
              <h1>客户</h1>
              <p>{count} 位客户</p>
            </div>
            <button
              type="button"
              className="customers-add-button"
              onClick={() => navigate(`/customer/form?returnTo=${encodeURIComponent(currentListPath)}`)}
              aria-label="新增客户"
              data-haptic="medium"
            >
              <AddOutline />
            </button>
          </header>

          <div className="customers-search-row">
            <label className="ios-search customers-search">
              <SearchOutline aria-hidden="true" />
              <input
                aria-label="搜索客户"
                placeholder="搜索姓名、村或手机号"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
              {keyword && (
                <button
                  type="button"
                  className="customers-search-clear"
                  onClick={() => setKeyword('')}
                >
                  清除
                </button>
              )}
            </label>
            <button type="button" className="customers-filter-button" onClick={openFilter} data-haptic="selection">
              <FilterOutline aria-hidden="true" />
              <span>筛选</span>
            </button>
          </div>
        </SafeArea>
      </section>

      <Popup
        visible={filterVisible}
        position="right"
        bodyClassName="customer-filter-popup"
        bodyStyle={{ width: 'min(60%, 258px)', height: '100%' }}
        onMaskClick={() => setFilterVisible(false)}
      >
        <aside className="customers-filter-drawer" aria-label="筛选客户">
          <div className="customers-filter-title">
            <strong>筛选客户</strong>
            <button
              type="button"
              onClick={() => setFilterVisible(false)}
              aria-label="关闭筛选"
            >
              <CloseOutline />
            </button>
          </div>

          <div className="customers-filter-sections">
            <section className="customers-filter-section">
              <h2>按姓氏</h2>
              <div className="customers-filter-grid">
                <FilterOption
                  active={draftSurname === ''}
                  onClick={() => setDraftSurname('')}
                >
                  全部
                </FilterOption>
                {data?.surnames.map((item) => (
                  <FilterOption
                    key={item.surname}
                    active={draftSurname === item.surname}
                    onClick={() => setDraftSurname(item.surname)}
                  >
                    {item.surname} {item.count}
                  </FilterOption>
                ))}
              </div>
            </section>

            <section className="customers-filter-section">
              <h2>按村</h2>
              <div className="customers-filter-grid">
                <FilterOption
                  active={draftVillage === ''}
                  onClick={() => setDraftVillage('')}
                >
                  全部
                </FilterOption>
                {data?.villages.map((item) => {
                  const village = item.village || '';
                  return (
                    <FilterOption
                      key={village || 'empty-village'}
                      active={draftVillage === village}
                      onClick={() => setDraftVillage(village)}
                    >
                      {village || '未填写'} {item.count}
                    </FilterOption>
                  );
                })}
              </div>
            </section>
          </div>

          <button type="button" className="customers-filter-submit" onClick={applyFilter} data-haptic="medium">
            查看结果
          </button>
        </aside>
      </Popup>

      {loading && !data ? (
        <div className="customers-loading">
          <Skeleton count={6} />
        </div>
      ) : loadError && !data ? (
        <EmptyState
          title="客户列表加载失败"
          description="请检查网络后重试"
          action={(
            <button type="button" className="ios-button ios-button-primary" onClick={() => void fetchData()}>
              重新加载
            </button>
          )}
        />
      ) : data && data.list.length === 0 ? (
        <EmptyState
          title="暂无客户"
          description={
            keyword || activeSurname || activeVillage
              ? '换个筛选条件试试'
              : '点击右上角新增客户'
          }
        />
      ) : (
        <main className="customers-list" aria-busy={loading}>
          {data?.list.map((customer, index) => (
            <ListItem
              key={customer.id}
              index={index}
              avatar={customer.avatar || undefined}
              avatarName={customer.name}
              avatarSize={52}
              avatarStyle={{ marginRight: 12, boxShadow: 'none' }}
              title={customer.name}
              subtitle={
                <div className="customer-list-meta">
                  <div className="customer-list-contact">
                    <span className="customer-list-location">
                      <EnvironmentOutline aria-hidden="true" />
                      <span>{customer.village || '未填写村'}</span>
                    </span>
                    <span className="customer-list-phone">
                      {customer.phone || '未填写电话'}
                    </span>
                  </div>
                  <div>
                    购买 {customer.buy_count} 次 · 累计 {customer.total_weight} 斤
                  </div>
                </div>
              }
              arrow
              onClick={() => navigate(customerDetailPath(customer.id))}
            />
          ))}
        </main>
      )}
    </div>
  );
}

function FilterOption({
  active,
  children,
  onClick
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`customers-filter-option${active ? ' is-active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
